import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, TextField, Button, IconButton, Tooltip, Modal, CircularProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { usePartnerIndex, PartnerTag } from '../components/partnerTag';   // [D-770] чий це юзер

const API = axios.create({ baseURL: '/api' });
API.interceptors.request.use(c => { const t = localStorage.getItem('dashboard_token'); if (t) c.headers['Authorization'] = 'Bearer ' + t; return c; });

// D-247: дата надання доступу (Europe/Dublin, як решта дашборду)
const fmtAccess = (s) => {
  if (!s) return null;
  try {
    return new Date(s).toLocaleString('uk-UA', { timeZone: 'Europe/Dublin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (e) { return null; }
};
// ефективний кінець доступу: пакет (subscription_end) або тріал (trial_end)
const endOf = (r) => r.subscription_end || r.trial_end || null;
// D-247: зворотній відлік дд:гг:хв + колір (≤5д жовтий, ≤3д червоний)
const fmtCountdown = (endStr, now) => {
  if (!endStr) return { text: '—', color: '#555', has: false };
  const ms = new Date(endStr).getTime() - now;
  if (ms <= 0) return { text: 'Закінчився', color: '#888', has: true };
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  const parts = [];
  if (days > 0) parts.push(days + 'д');
  if (days > 0 || hours > 0) parts.push(hours + 'г');
  parts.push(mins + 'хв');
  const daysLeft = ms / 86400000;
  let color = '#4caf50';
  if (daysLeft <= 3) color = '#f44336';
  else if (daysLeft <= 5) color = '#ffe57f';
  return { text: parts.join(' '), color, has: true };
};

export default function ReceiptsPage() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [imgModal, setImgModal] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [confirmAmount, setConfirmAmount] = useState('');
  const [now, setNow] = useState(Date.now()); // D-247: живий відлік
  const { of, color } = usePartnerIndex();   // [D-770]

  const load = (params = {}) => {
    setLoading(true);
    const p = new URLSearchParams();
    if (params.search) p.set('search', params.search);
    if (params.dateFrom) p.set('date_from', params.dateFrom);
    if (params.dateTo) p.set('date_to', params.dateTo);
    API.get('/receipts?' + p).then(r => { setReceipts(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);
  // D-247: оновлювати відлік щохвилини
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(t); }, []);

  const confirm = async (id, val, amount) => {
    const params = new URLSearchParams({ confirmed: val });
    if (amount) params.set('amount', amount);
    await API.patch('/receipts/' + id + '/confirm?' + params);
    setReceipts(r => r.map(x => x.id===id ? {...x, payment_confirmed: val, amount: amount||x.amount} : x));
    setConfirmDialog(null); setConfirmAmount('');
  };

  const del = async (id) => {
    if (!window.confirm('Видалити квитанцію?')) return;
    await API.delete('/receipts/' + id);
    setReceipts(r => r.filter(x => x.id !== id));
  };

  // D-247: сортування за залишком часу — спершу активні (найближчі до завершення),
  // потім вже закінчені, потім без дати кінця.
  const remainMs = (r) => { const e = endOf(r); return e ? (new Date(e).getTime() - now) : null; };
  const rank = (r) => { const ms = remainMs(r); if (ms == null) return 3; if (ms <= 0) return 2; return 1; };
  const sorted = [...receipts].sort((a, b) => {
    const Ra = rank(a), Rb = rank(b);
    if (Ra !== Rb) return Ra - Rb;
    const ma = remainMs(a), mb = remainMs(b);
    if (Ra === 1) return ma - mb;            // активні: найменший залишок зверху
    if (Ra === 2) return mb - ma;            // закінчені: ті що недавно — вище
    return (b.created_at || '').localeCompare(a.created_at || ''); // без дати: новіші зверху
  });

  return (
    <Box>
      <Typography variant="h5" sx={{ color:'#fff', fontWeight:'bold', mb:2 }}>🧾 Квитанції</Typography>
      <Paper sx={{ bgcolor:'#1a1a2e', border:'1px solid #333', p:2, mb:2 }}>
        <Box sx={{ display:'flex', gap:1, flexWrap:'wrap', alignItems:'center' }}>
          <TextField size="small" placeholder="Пошук юзера або суми..." value={search}
            onChange={e => setSearch(e.target.value)} onKeyPress={e => e.key==='Enter' && load({search,dateFrom,dateTo})}
            sx={{ input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'}, minWidth:200 }}/>
          <TextField size="small" type="date" label="Від" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            InputLabelProps={{shrink:true}} sx={{ input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          <TextField size="small" type="date" label="До" value={dateTo} onChange={e => setDateTo(e.target.value)}
            InputLabelProps={{shrink:true}} sx={{ input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#333'} }}/>
          <Button variant="contained" onClick={() => load({search,dateFrom,dateTo})} sx={{ bgcolor:'var(--ac1, #7c4dff)' }} startIcon={<SearchIcon/>}>Знайти</Button>
          <Button variant="outlined" onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); load(); }} sx={{ borderColor:'#555', color:'#aaa' }}>Скинути</Button>
          <Chip label={receipts.length + ' квитанцій'} sx={{ bgcolor:'rgba(var(--ac1rgb, 124, 77, 255),0.2)', color:'var(--ac1, #7c4dff)' }}/>
          <Typography variant="caption" sx={{ color:'#666' }}>↕️ Сортовано за залишком терміну (найближчі до завершення — зверху)</Typography>
        </Box>
      </Paper>
      {loading ? <CircularProgress sx={{ color:'var(--ac1, #7c4dff)', m:4 }}/> :
        receipts.length === 0 ? <Typography sx={{ color:'#555', p:4, textAlign:'center' }} style={{background:"rgba(26,26,46,0.7)",padding:"12px 24px",borderRadius:8}}>Немає квитанцій</Typography> :
        sorted.map(r => {
          const cd = fmtCountdown(endOf(r), now);
          const acc = fmtAccess(r.confirmed_at);
          return (
            <Paper key={r.id} sx={{ bgcolor:'#1a1a2e', border:'1px solid #333', p:1.5, mb:0.5, display:'flex', alignItems:'center', gap:2 }}>
              <Box sx={{ flexGrow:1 }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:.6, flexWrap:'wrap' }}>
                  <Typography variant="body2" sx={{ color:'#fff' }}>@{r.telegram_username || '?'}</Typography>
                  <PartnerTag who={of(r.user_id)} color={color}/>{/* [D-770] */}
                </Box>
                {r.package_name && <Typography variant="caption" sx={{ color:'#FFD700', display:'block' }}>📦 {r.package_name} • €{r.amount || r.package_price || 0}</Typography>}
                {r.confirmed_by === 'mono_auto' && <Typography variant="caption" sx={{ color:'#2196f3', display:'block' }}>💳 Картка (Monobank)</Typography>}
                {(r.package_daily != null || r.user_unlimited || r.user_daily != null) && <Typography variant="caption" sx={{ color:'#9fa8da', display:'block' }}>🎬 {r.package_daily != null ? (r.package_daily === 0 ? 'індивід.' : r.package_daily) : (r.user_unlimited ? '∞' : r.user_daily)} відео/день</Typography>}
                {acc && <Typography variant="caption" sx={{ color:'#80cbc4', display:'block' }}>🟢 Доступ з: {acc}</Typography>}
                {cd.has && <Typography variant="caption" sx={{ display:'block', fontWeight:'bold', color: cd.color }}>⏳ Залишилось: {cd.text}</Typography>}
                <Typography variant="caption" sx={{ color:'#666' }}>
                  🧾 {r.created_at ? r.created_at.substring(0,16).replace('T',' ') : ''} {r.amount ? '• €'+r.amount : ''}
                </Typography>
              </Box>
              <Tooltip title={r.payment_confirmed ? "Скасувати підтвердження" : "Підтвердити отримання"}>
                <IconButton size="small"
                  onClick={() => r.payment_confirmed ? confirm(r.id, false) : setConfirmDialog(r)}
                  sx={{ color: r.payment_confirmed && r.amount > 0 ? '#4caf50' : r.payment_confirmed && r.amount <= 0 ? '#f44336' : '#555', '&:hover':{ color: 'var(--ac1, #7c4dff)' } }}>
                  <CheckCircleIcon/>
                </IconButton>
              </Tooltip>
              {r.confirmed_by === 'mono_auto'
                ? <Tooltip title="Оплата карткою (Monobank)"><span style={{fontSize:20,lineHeight:'1',cursor:'default',userSelect:'none'}}>💳</span></Tooltip>
                : <Tooltip title="Переглянути квитанцію">
                    <IconButton size="small" onClick={() => setImgModal(r.id)} sx={{ color:'var(--ac1, #7c4dff)' }}><ImageIcon/></IconButton>
                  </Tooltip>
              }
              <Tooltip title="Видалити">
                <IconButton size="small" onClick={() => del(r.id)} sx={{ color:'#555','&:hover':{color:'#f44'} }}><DeleteIcon fontSize="small"/></IconButton>
              </Tooltip>
            </Paper>
          );
        })
      }
      <Modal open={!!imgModal} onClose={() => setImgModal(null)}>
        <Box sx={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', bgcolor:'#1a1a2e', border:'1px solid #333', p:2, borderRadius:2, maxWidth:'90vw', maxHeight:'90vh' }}>
          <img src={'/api/receipts/image/' + imgModal + '?token=' + localStorage.getItem('dashboard_token')} alt="receipt"
            style={{ maxWidth:'80vw', maxHeight:'80vh', objectFit:'contain' }}/>
          <Button fullWidth onClick={() => setImgModal(null)} sx={{ mt:1, color:'#aaa' }}>Закрити</Button>
        </Box>
      </Modal>
      <Dialog open={!!confirmDialog} onClose={() => setConfirmDialog(null)}>
        <DialogTitle sx={{ bgcolor:'#1a1a2e', color:'#fff' }}>💰 Підтвердити отримання</DialogTitle>
        <DialogContent sx={{ bgcolor:'#1a1a2e' }}>
          <Typography sx={{ color:'#aaa', mb:1 }}>@{confirmDialog?.telegram_username}</Typography>
          <TextField fullWidth size="small" label="Сума ($)" type="number" value={confirmAmount}
            onChange={e => setConfirmAmount(e.target.value)} autoFocus
            sx={{ input:{color:'#fff'}, '& .MuiOutlinedInput-notchedOutline':{borderColor:'#555'}, label:{color:'#aaa'} }}/>
        </DialogContent>
        <DialogActions sx={{ bgcolor:'#1a1a2e' }}>
          <Button onClick={() => setConfirmDialog(null)} sx={{ color:'#aaa' }}>Скасувати</Button>
          <Button onClick={() => confirm(confirmDialog.id, true, confirmAmount)} variant="contained" sx={{ bgcolor:'#4caf50' }}>✅ Підтвердити</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
